import { uniqBy } from 'lodash'
import { useMemo, useState } from 'react'

import { OnCallType, ShiftActivity } from '@bemlo/enums'
import { serializeShift } from '@bemlo/schedule'

export type ShiftPreset = {
  key: string
  activity: ShiftActivity
  startTime: string
  endTime: string
  breakTime: number
  comment: string
  scheduleResourceId: string
  persistedId: string | undefined
  tags: { id: string }[]
  onCallTime:
    | {
        type: OnCallType
        startTime: string
        endTime: string
      }
    | null
    | undefined
  // tags: string[] // BEMLO-3276: Add support for tags
}

export type ShiftPresetInput = Omit<ShiftPreset, 'key' | 'persistedId'>

type PersistedShiftPresetInput = ShiftPresetInput & { id: string }

type ShiftPresetWithoutKey = ShiftPresetInput & { persistedId?: string }

const toPresetKey = ({ scheduleResourceId, ...input }: ShiftPresetInput) =>
  serializeShift({ ...input, date: 'PRESET' }, { scheduleResourceId })

export const useShiftPresets = ({
  shifts,
  persisted = [],
}: {
  shifts: ShiftPresetInput[]
  persisted?: PersistedShiftPresetInput[]
}) => {
  const [draft, setDraft] = useState<ShiftPresetInput[]>([])

  return useMemo(() => {
    const combinedData: ShiftPresetWithoutKey[] = [
      ...shifts,
      ...draft,
      ...persisted.map(({ id, ...data }) => ({
        ...data,
        persistedId: id,
      })),
    ]

    const withKey: ShiftPreset[] = combinedData.map((data) => {
      return {
        key: toPresetKey(data),
        activity: data.activity,
        startTime: data.startTime,
        endTime: data.endTime,
        breakTime: data.breakTime,
        comment: data.comment,
        tags: data.tags,
        scheduleResourceId: data.scheduleResourceId,
        persistedId: data.persistedId,
        onCallTime: data.onCallTime
          ? {
              type: data.onCallTime.type,
              startTime: data.onCallTime.startTime,
              endTime: data.onCallTime.endTime,
            }
          : undefined,
      }
    })

    // Saved presets should render at the top of the list, then sorted by time and properties
    const sorted = withKey.sort((a, b) => {
      if (a.persistedId && !b.persistedId) {
        return -1
      }

      if (!a.persistedId && b.persistedId) {
        return 1
      }

      if (a.startTime !== b.startTime) {
        return a.startTime.localeCompare(b.startTime)
      }

      if (a.endTime !== b.endTime) {
        return a.endTime.localeCompare(b.endTime)
      }

      if (a.breakTime !== b.breakTime) {
        return a.breakTime - b.breakTime
      }

      if (!a.onCallTime && b.onCallTime) {
        return -1
      }

      if (a.onCallTime && !b.onCallTime) {
        return 1
      }

      if (a.onCallTime && b.onCallTime) {
        if (a.onCallTime.startTime !== b.onCallTime.startTime) {
          return a.onCallTime.startTime.localeCompare(b.onCallTime.startTime)
        }

        if (a.onCallTime.endTime !== b.onCallTime.endTime) {
          return a.onCallTime.endTime.localeCompare(b.onCallTime.endTime)
        }
      }

      return a.comment.localeCompare(b.comment)
    })

    // Remove duplicates, keeping the ones with persistedId
    // important that list is sorted before this step
    // to keep the persisted ones at the top
    // For now we only allowed presets of type WORK
    const unique = uniqBy(sorted, 'key').filter(
      (preset) => preset.activity == ShiftActivity.WORK,
    )

    // Determine if break times differ for the same start and end times which means
    // badges should be displayed for the user to select the correct break time
    const hasParallelPresetsWithDifferentBreakTimes = (() => {
      const breaksBySlot = unique.reduce(
        (acc, shift) => {
          const key = `${shift.startTime}-${shift.endTime}`
          if (!acc[key]) {
            acc[key] = new Set<number>()
          }
          acc[key].add(shift.breakTime)
          return acc
        },
        {} as Record<string, Set<number>>,
      )

      return Object.values(breaksBySlot).some(
        (breakTimes) => breakTimes.size > 1,
      )
    })()

    const createPreset = (input: ShiftPresetInput): ShiftPreset => {
      const key = toPresetKey(input)
      const existing = unique.find((preset) => preset.key === key)

      if (existing) return existing

      setDraft((curr) => [...curr, input])
      return { ...input, key, persistedId: undefined }
    }

    return {
      presets: unique,
      hasParallelPresetsWithDifferentBreakTimes,
      createPreset,
    }
  }, [shifts, persisted, draft])
}
